/**
 * One reading of "what went wrong" for every failed request (spec 21 §3.9.3).
 *
 * The axios interceptor in `@/api/client` turns an API error envelope into an Error with
 * `status`, `code`, `details` and `traceId`. Errors that never reached the API (network
 * down, dev proxy refused, timeout) are raw AxiosErrors with no envelope. Both shapes are
 * read here so screens never have to know which one they got.
 */

export type ErrorKind =
  | 'network'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'rate_limited'
  | 'server'
  | 'client'
  | 'unknown'

export interface ErrorInfo {
  kind: ErrorKind
  status?: number
  code?: string
  traceId?: string
  /** A plain-language sentence safe to show the user. Never a stack or a raw body. */
  message: string
  /** Short heading for a full-panel error. */
  title: string
  /** Field-level validation messages, keyed by the API field name. */
  fieldErrors: Record<string, string>
}

interface MaybeApiError {
  message?: string
  status?: number
  code?: string
  traceId?: string
  details?: unknown
  response?: { status?: number; data?: unknown }
  isAxiosError?: boolean
}

export function errorStatus(err: unknown): number | undefined {
  const e = err as MaybeApiError | null
  return e?.status ?? e?.response?.status
}

function fieldErrorsOf(details: unknown): Record<string, string> {
  const out: Record<string, string> = {}
  if (!details || typeof details !== 'object' || Array.isArray(details)) return out
  for (const [k, v] of Object.entries(details as Record<string, unknown>)) {
    if (Array.isArray(v) && typeof v[0] === 'string') out[k] = v[0]
    else if (typeof v === 'string') out[k] = v
  }
  return out
}

const TITLES: Record<ErrorKind, string> = {
  network: "Can't reach the server",
  unauthenticated: 'Your session has ended',
  forbidden: "You don't have access to this",
  not_found: 'Not found',
  conflict: 'This changed while you were working',
  validation: 'Some details need fixing',
  rate_limited: 'Too many requests',
  server: 'Something went wrong on our side',
  client: "That request couldn't be completed",
  unknown: 'Something went wrong',
}

const DEFAULT_MESSAGES: Record<ErrorKind, string> = {
  network: 'Check your connection and try again. Nothing has been changed.',
  unauthenticated: 'Please sign in again to continue.',
  forbidden: "You don't have access to this. If you need it, ask your administrator.",
  not_found: 'This record was deleted, or you no longer have access to it.',
  conflict: 'Someone else changed this record. Reload to see the latest version.',
  validation: 'Please correct the highlighted fields and try again.',
  rate_limited: 'Please wait a few seconds and try again.',
  server: 'The server hit an error. Try again; if it keeps happening, share the reference with support.',
  client: 'The request was not accepted. Try again, or share the reference below with support.',
  unknown: 'Try again. If it keeps happening, share the reference below with support.',
}

export function describeError(err: unknown): ErrorInfo {
  const e = (err ?? {}) as MaybeApiError
  const status = errorStatus(err)
  let kind: ErrorKind
  if (!status) {
    kind = e.isAxiosError || /network|timeout|ECONN/i.test(e.message ?? '') ? 'network' : 'unknown'
  } else if (status === 401 || status === 419) kind = 'unauthenticated'
  else if (status === 403) kind = 'forbidden'
  else if (status === 404) kind = 'not_found'
  else if (status === 409) kind = 'conflict'
  else if (status === 422) kind = 'validation'
  else if (status === 429) kind = 'rate_limited'
  else if (status >= 500) kind = 'server'
  else kind = 'client'

  const hasEnvelope = Boolean(e.code)
  // QA-M01: a pending database update is not "an error on our side" — say what it is.
  if (status === 503 && (e.details as { reason?: string } | undefined)?.reason === 'DATABASE_UPDATE_PENDING') {
    return {
      kind: 'server', status, code: e.code, traceId: e.traceId, fieldErrors: {},
      title: 'This part is being updated',
      message: 'This feature is unavailable until the server finishes an update. Your administrator has been told; nothing you entered was lost.',
    }
  }
  // Server messages from the contract envelope are written for users; raw axios messages
  // ("Request failed with status code 500") and 5xx internals are not.
  // A 403 keeps our generic wording, EXCEPT a specific, role-free reason the server chose to give
  // (a switched-off module, platform-level data): that tells the user what to do next. Server 403s
  // never name roles (SEC-008); the generic role refusal is still replaced.
  const specific403 = kind === 'forbidden' && hasEnvelope && e.message && !/^You do not have access to this resource\.?$/i.test(e.message)
  const serverMsg = hasEnvelope && kind !== 'server' && (kind !== 'forbidden' || specific403) ? e.message : undefined
  // Permission messages must not reveal roles (contract defect #11): always our wording.
  const message = kind === 'validation'
    ? (serverMsg && serverMsg !== 'The given data was invalid.' ? serverMsg : DEFAULT_MESSAGES.validation)
    : serverMsg ?? (kind === 'unknown' && e.message && !/status code/i.test(e.message) ? e.message : DEFAULT_MESSAGES[kind])

  return {
    kind,
    status,
    code: e.code,
    traceId: e.traceId,
    message,
    title: TITLES[kind],
    fieldErrors: kind === 'validation' ? fieldErrorsOf(e.details) : {},
  }
}

/** A one-line message for a toast or an inline form banner. */
export function errorMessage(err: unknown): string {
  const info = describeError(err)
  if (info.kind === 'validation' && Object.keys(info.fieldErrors).length) {
    return Object.values(info.fieldErrors)[0]
  }
  return info.message
}
